/**
 * Form pieces (design system, web): one look for every text box, select, text
 * area and tick box on a settings or form screen, so pages stop hand-rolling
 * their own. Built on the shared tokens and text roles:
 *
 * - `FormField` — a label above its control, an optional hint under it and an
 *   error in `dangerText`. The label is the `label` role's size, weight and
 *   colour in sentence case (as the Customers forms); a required field shows
 *   a `*` that is not read out (the control says aria-required instead).
 * - `TextInput`, `Select`, `TextArea` — native controls on `bgSubtle` with a
 *   1px `border`, `value` text, a `focus` ring; `invalid` paints the border
 *   `danger`. 40px tall on a computer, 44px on a phone (the stylesheet below).
 * - `Checkbox` — a native tick box (accent `brandFill`) in a 44px row, with an
 *   optional hint read out with it.
 * - `fieldControlStyle` — the same look for a control a page must build itself
 *   (a number box inside a sentence).
 *
 * `FormField` is the one promoted from components/ui/form (that path re-exports
 * it; the older components/ui/FormField draws with these pieces too).
 *
 * The on/off `Switch` is the shared one from @turn-connect/ui, exported here
 * too; `SettingRow` puts a setting's name and help beside its control.
 */
import React, { useId } from 'react';
import { compactSpec, textRole, tokenVars, type Density } from '@turn-connect/ui';
import { useDensity } from './density';
import { fieldStyle } from './fieldStyle';

const FIELD_CLASS = 'tc-field';
const STYLE_ID = 'tc-ds-field-css';

/** Heights and focus ring the inline styles cannot express (media query, :focus-visible, placeholder). */
export const FIELD_CSS = `.${FIELD_CLASS}{min-height:40px}
.${FIELD_CLASS}[data-density="compact"]{min-height:${compactSpec.controlHeight}px}
textarea.${FIELD_CLASS}{min-height:72px}
@media (max-width:${compactSpec.touchMaxWidth}px){.${FIELD_CLASS},.${FIELD_CLASS}[data-density="compact"]{min-height:44px}}
.${FIELD_CLASS}:focus-visible{outline:2px solid ${tokenVars.focus};outline-offset:1px;border-color:${tokenVars.focus}}
.${FIELD_CLASS}::placeholder{color:${tokenVars.text.muted};opacity:1}
.${FIELD_CLASS}:disabled{color:${tokenVars.text.disabled};cursor:not-allowed}
.tc-check{accent-color:${tokenVars.brandFill}}
.tc-check:focus-visible{outline:2px solid ${tokenVars.focus};outline-offset:2px}`;

/** Adds the stylesheet once per document. */
export function ensureFieldCss(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = FIELD_CSS;
  document.head.appendChild(el);
}

/**
 * The control look (text box, select, text area) for a given state: the ds
 * `fieldStyle` (./fieldStyle, the Customers and admin fields) with a `danger`
 * border when invalid and a muted ground when disabled. The height comes from
 * the `tc-field` class (40px, 44px on a phone), so it is not set inline.
 */
export function fieldControlStyle(
  opts: { invalid?: boolean; disabled?: boolean; width?: React.CSSProperties['width'] } = {}
): React.CSSProperties {
  return {
    ...fieldStyle,
    minHeight: undefined,
    width: opts.width ?? '100%',
    fontFamily: 'inherit',
    border: `1px solid ${opts.invalid ? tokenVars.danger : tokenVars.border}`,
    background: opts.disabled ? tokenVars.surfaceMuted : tokenVars.bgSubtle,
  };
}

/** A form field's label: the `label` role in sentence case. */
export const fieldLabelStyle: React.CSSProperties = {
  ...textRole('label'),
  textTransform: 'none',
  letterSpacing: 'normal',
  display: 'block',
};

export interface FormFieldProps {
  label: React.ReactNode;
  /** The control's id, so the label names it. Generated (and passed to a single child control) when left out. */
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  /** Hides the visible label (still read out). For dense layouts. */
  hideLabel?: boolean;
  testId?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const FormField: React.FC<FormFieldProps> = ({ label, htmlFor, hint, error, required, hideLabel, testId, style, children }) => {
  const generated = useId();
  const only = React.Children.count(children) === 1 && React.isValidElement(children) ? (children as React.ReactElement<any>) : null;
  const id = htmlFor ?? only?.props.id ?? generated;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control =
    only && !htmlFor
      ? React.cloneElement(only, {
          id,
          'aria-describedby': only.props['aria-describedby'] ?? describedBy,
          'aria-required': only.props['aria-required'] ?? (required || undefined),
          required: only.props.required ?? (required || undefined),
          'aria-invalid': only.props['aria-invalid'] ?? (error ? true : undefined),
        })
      : children;
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, ...style }}>
      <span style={hideLabel ? visuallyHidden : { display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <label htmlFor={id} style={fieldLabelStyle}>
          {label}
        </label>
        {/* Outside the label, so the field's name is the label alone. */}
        {required && (
          <span aria-hidden="true" style={{ ...fieldLabelStyle, color: tokenVars.dangerText }}>
            *
          </span>
        )}
      </span>
      {control}
      {hint && !error && (
        <span id={hintId} style={textRole('meta')}>
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} role="alert" style={textRole('meta', tokenVars.dangerText)}>
          {error}
        </span>
      )}
    </div>
  );
};

interface ControlExtras {
  /** Paints the border `danger` (and sets aria-invalid). */
  invalid?: boolean;
  /** `compact`: 36px on a computer, 44px on a phone. Defaults to the nearest DensityProvider. */
  density?: Density;
}

export type TextInputProps = React.InputHTMLAttributes<HTMLInputElement> & ControlExtras & { 'data-testid'?: string };

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { invalid, density, style, className, ...rest },
  ref
) {
  ensureFieldCss();
  const compact = useDensity(density) === 'compact';
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      {...rest}
      data-density={compact ? 'compact' : undefined}
      className={className ? `${FIELD_CLASS} ${className}` : FIELD_CLASS}
      style={{ ...fieldControlStyle({ invalid, disabled: rest.disabled }), ...style }}
    />
  );
});

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & ControlExtras & { 'data-testid'?: string };

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, density, style, className, children, ...rest },
  ref
) {
  ensureFieldCss();
  const compact = useDensity(density) === 'compact';
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      {...rest}
      data-density={compact ? 'compact' : undefined}
      className={className ? `${FIELD_CLASS} ${className}` : FIELD_CLASS}
      style={{ ...fieldControlStyle({ invalid, disabled: rest.disabled }), cursor: rest.disabled ? 'not-allowed' : 'pointer', ...style }}
    >
      {children}
    </select>
  );
});

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & ControlExtras & { 'data-testid'?: string };

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { invalid, density: _density, style, className, ...rest },
  ref
) {
  ensureFieldCss();
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      {...rest}
      className={className ? `${FIELD_CLASS} ${className}` : FIELD_CLASS}
      style={{ ...fieldControlStyle({ invalid, disabled: rest.disabled }), resize: 'vertical', ...style }}
    />
  );
});

export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  /** One line under the label, read out with it. */
  hint?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  name?: string;
  testId?: string;
  style?: React.CSSProperties;
}

export const Checkbox: React.FC<CheckboxProps> = ({ checked, onChange, label, hint, disabled = false, id, name, testId, style }) => {
  ensureFieldCss();
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = hint ? `${inputId}-hint` : undefined;
  return (
    <label
      htmlFor={inputId}
      style={{
        display: 'flex',
        alignItems: hint ? 'flex-start' : 'center',
        gap: 10,
        minHeight: 44,
        paddingBlock: hint ? 6 : 0,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      <input
        id={inputId}
        type="checkbox"
        className="tc-check"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        // Named by the label alone; the hint is its description.
        aria-labelledby={`${inputId}-label`}
        aria-describedby={hintId}
        data-testid={testId}
        style={{ width: 18, height: 18, margin: hint ? '1px 0 0' : 0, flexShrink: 0 }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span id={`${inputId}-label`} style={textRole('value')}>
          {label}
        </span>
        {hint && (
          <span id={hintId} style={textRole('meta')}>
            {hint}
          </span>
        )}
      </span>
    </label>
  );
};
